import type { Container } from './container.js';
import type { Disposable } from '../core/disposable.js';
import { joinBase, stripBase } from './paths.js';

/**
 * Context handed to a lazily-imported view when it mounts.
 */
export interface ViewContext {
  readonly container: Container;
  readonly params: Readonly<Record<string, string>>;
  /** Aborted when the user navigates away mid-load (cancellation discipline). */
  readonly signal: AbortSignal;
  /** The DOM node the view renders into. */
  readonly outlet: HTMLElement;
  /** Programmatic navigation for in-view links/commands. */
  readonly navigate: (path: string) => void;
  /** Set the document title for this view (app name is appended). */
  readonly setTitle: (title: string) => void;
}

/** A view module's contract: mount returns a Disposable for teardown. */
export interface View {
  mount(ctx: ViewContext): Promise<Disposable> | Disposable;
}

interface Route {
  readonly pattern: RegExp;
  readonly keys: readonly string[];
  /** Lazy import — views are code-split via dynamic import. */
  readonly load: () => Promise<{ default: View }>;
}

/** Build a route from a path template like `/people/:id`. */
function route(template: string, load: Route['load']): Route {
  const keys: string[] = [];
  const pattern = new RegExp(
    '^' +
      template.replace(/:[A-Za-z0-9_]+/g, (m) => {
        keys.push(m.slice(1));
        return '([^/]+)';
      }) +
      '/?$',
  );
  return { pattern, keys, load };
}

/**
 * Minimal History-based router with lazy routes and SPA fallback (same
 * discipline as the Idea Engine's):
 *
 * - Views are dynamic imports (code-split); a loading state shows while importing.
 * - Each navigation gets its own AbortController; navigating away aborts the
 *   previous in-flight load (navigate-race discipline).
 * - Unknown paths render the not-found state.
 */
export class Router {
  #routes: Route[] = [];
  #current: Disposable | null = null;
  #activeController: AbortController | null = null;
  #token = 0;

  constructor(
    private readonly container: Container,
    private readonly outlet: HTMLElement,
  ) {}

  add(template: string, load: Route['load']): this {
    this.#routes.push(route(template, load));
    return this;
  }

  /** Current internal (base-relative) path from the on-page location. */
  #currentPath(): string {
    return stripBase(this.container.config.basePath, window.location.pathname);
  }

  /** Start listening to history and resolve the current location. */
  start(): void {
    window.addEventListener('popstate', () => void this.resolve(this.#currentPath()));
    void this.resolve(this.#currentPath());
  }

  /** Navigate to an internal (base-relative) path, e.g. `/people/123`.
   *  The query string is preserved so boot-time knobs (`?lang=ar`) survive. */
  navigate(path: string): void {
    const target = joinBase(this.container.config.basePath, path);
    if (target === window.location.pathname) return;
    window.history.pushState({}, '', `${target}${window.location.search}`);
    void this.resolve(path);
  }

  private renderMessage(text: string): void {
    this.outlet.replaceChildren();
    const p = document.createElement('p');
    p.className = 'view-status';
    p.textContent = text; // textContent — never untrusted HTML
    this.outlet.appendChild(p);
  }

  private setTitle(title: string): void {
    document.title = title ? `${title} — ${this.container.messages.appTitle}` : this.container.messages.appTitle;
  }

  /** Move focus to the new view's heading so AT announces the route change. */
  private focusView(): void {
    const heading = this.outlet.querySelector<HTMLElement>('h1, h2');
    if (!heading) return;
    if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
    heading.focus({ preventScroll: true });
  }

  private async resolve(path: string): Promise<void> {
    const token = ++this.#token;

    // Cancel any in-flight navigation's work for real (not just guard its result).
    this.#activeController?.abort();
    const controller = new AbortController();
    this.#activeController = controller;

    // Tear down the previous view (Disposable convention).
    this.#current?.dispose();
    this.#current = null;

    const matched = this.match(path);
    if (!matched) {
      this.setTitle(this.container.messages.empty);
      this.renderMessage(this.container.messages.empty);
      return;
    }

    this.renderMessage(this.container.messages.loading);
    try {
      const mod = await matched.route.load();
      // Navigate-race: bail if a newer navigation superseded us.
      if (token !== this.#token) return;
      this.outlet.replaceChildren();
      const disposable = await mod.default.mount({
        container: this.container,
        params: matched.params,
        signal: controller.signal,
        outlet: this.outlet,
        navigate: (p) => this.navigate(p),
        setTitle: (t) => this.setTitle(t),
      });
      if (token !== this.#token) {
        disposable.dispose();
        return;
      }
      this.#current = disposable;
      this.focusView();
    } catch (cause) {
      if (token !== this.#token) return;
      this.container.telemetry.error({ kind: 'unknown', message: 'View failed to load', cause });
      this.renderMessage(this.container.messages.errorGeneric);
    }
  }

  private match(path: string): { route: Route; params: Record<string, string> } | null {
    for (const r of this.#routes) {
      const m = r.pattern.exec(path);
      if (!m) continue;
      const params: Record<string, string> = {};
      r.keys.forEach((k, i) => {
        params[k] = decodeURIComponent(m[i + 1] ?? '');
      });
      return { route: r, params };
    }
    return null;
  }
}
